import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { PulseLoader } from "react-spinners";
import TableHeader from "../table-header/TableHeader";
import ViewAllRow from "../../view-all-row/ViewAllRow";
import NoResultsRow from "../../no-results-row/NoResultsRow";
import { NoProjectsImage } from "../../../assets/icons";
import ExpandableRowNew from "../expandable-row/expandable-row-new";
import "./expandable-table.styles.scss";


let renderCount = 0;

const ExpandableTableNew = ({
  data,
  selectedData,
  isFetchingData,
  groupBy,
  addMultipleSelectedDataItems,
  deleteMultipleSelectedDataItems,
  sortedFilteredData,
  filtersApplied,
  tableDataGroups,
  callback,
  keyFunction,
  pageIdentifier,
  clearProjectsFilter,
  refreshTable,
  TableRow,
  projectsCount,
  labels
}) => {
    const [filteredProjects, setFilteredProjects] = useState(true);

    // Determines if any filter is being applied
    useEffect(() => {
        let newFilteredProjects = false;
        let filteredProjectsCount = 0;

        if (!data || isFetchingData || !sortedFilteredData) return;

        if (sortedFilteredData) {
            for (const type in sortedFilteredData) {
                if (sortedFilteredData[type].length > 0) {
                    filteredProjectsCount += 1;
                    newFilteredProjects = true;
                }
            }
        }

        setFilteredProjects(newFilteredProjects || (filteredProjectsCount > 0))
    }, [sortedFilteredData])

    // Callback function to refetch data
    useEffect(() => {
        refreshTable(true);
    }, []);

    // Determines if the default grouping (category) and filtering (priority) is being applied upon change in projects
    useEffect(() => {
        checkDefaultTabToOpen();
    },[data, groupBy]);

    const setNotFoundMsg = () => {
        switch (pageIdentifier) {
            case "project":
                return `No ${labels?.ProjectPlural ?? "projects"} match your filter. `
            case "task":
                return `No ${labels?.TaskPlural ?? "tasks"} match your filter. `
            default:
                return "No items match your filter. "
        }
    }

    // Handles clicking the checkbox on the accordion headers to select all projects under a grouping category
    const onSelectAll = (e, type, isIndeterminate) => {
        let isChecked = e.target.checked;
        let allIdsOfType;
        let allSelectedIdsOfType;
        if (pageIdentifier === 'task') {
            allIdsOfType = sortedFilteredData[type].map(task => keyFunction(task));
            allSelectedIdsOfType = allIdsOfType.filter(key => selectedData.includes(key));
        } else {
            allIdsOfType = sortedFilteredData[type].map(project => project.Id);
            allSelectedIdsOfType = allIdsOfType.filter(id => selectedData.includes(id));
        }

        if (isIndeterminate) {
            document.getElementById(`${pageIdentifier}-table-checkbox-${type}`).checked = false;
            deleteMultipleSelectedDataItems(allSelectedIdsOfType);
        } else if (isChecked) {
            addMultipleSelectedDataItems(allIdsOfType);

            callback({type:'show_' + type.toLowerCase(), payload: true, actionType: 'set'})
            callback({type:'viewAll_' + type.toLowerCase(), payload: true, actionType: 'set'})
        } else {
            deleteMultipleSelectedDataItems(allIdsOfType);
        }
    }

    const setAllRows = (value, rowType) => {
        for (let group in tableDataGroups) {
            for (let subgroup of tableDataGroups[group]) {
                callback({type: rowType + "_" + subgroup.type, payload: value, actionType: "set"})
            }
        }
    }

    // Minimizes all accordions for groupby
    const minimizeAllViewAllRows = () => {
        setAllRows(false, "viewAll");
    }

    // Expands a given accordion when header is clicked
    const setShowTab = (type) => {
        callback({type:"show_" + type, payload: false, actionType: "toggle"});
    };

    // Determines which accordion to open for grouping based on number of projects under that tab
    const checkDefaultTabToOpen = () => { // check if default open tab has values, if not close it and open next tab
        minimizeAllViewAllRows();

        const group = tableDataGroups[groupBy.toLowerCase()]

        let flag = false;
        for (let i = 0; i < group.length; i++) {
            let value = false

            const subgroup = group[i];

            if (((sortedFilteredData[subgroup.type] && sortedFilteredData[subgroup.type].length > 0) || (i === group.length - 1)) && !flag) {
                value = true;
                flag = true;
            }
            callback({type: "show_" + subgroup.type, payload: value, actionType: "set"})
        }
    }

    const renderData = () => {
        return sortedFilteredData &&
        <>
        {   tableDataGroups[groupBy.toLowerCase()].map((grouping) => {
                return (
                    <React.Fragment key={grouping.type}>
                        {   grouping.type !== "none" ?
                                <ExpandableRowNew
                                    pageIdentifier={pageIdentifier}
                                    type={grouping.type}
                                    data={sortedFilteredData[grouping.type]}
                                    selectedData={selectedData}
                                    table={pageIdentifier}
                                    showTab={grouping.state}
                                    setShowTab={setShowTab}
                                    onSelectAll={onSelectAll}
                                    keyFunction={keyFunction}
                                    projectsCount={projectsCount}
                                    labels={labels}
                                />
                            :
                                <React.Fragment key={grouping.type} />
                        }
                        {   (grouping.state && sortedFilteredData[grouping.type]) &&
                            sortedFilteredData[grouping.type].map((project, index) => {
                                let numRows = grouping.viewAll || grouping.type === "none"
                                    ? sortedFilteredData[grouping.type].length
                                    : 10;

                                return index < numRows ?
                                    <TableRow data={project} key={keyFunction(project)} />
                                :
                                    <React.Fragment key={keyFunction(project)} />
                            })
                        }
                        {   grouping.state && sortedFilteredData[grouping.type].length <= 0 &&
                            <NoResultsRow />
                        }
                        {   grouping.type !== "none" && sortedFilteredData[grouping.type] &&
                            grouping.state && sortedFilteredData[grouping.type].length > 0 &&
                            <ViewAllRow
                                type={grouping.type}
                                setviewAll={callback}
                                viewAll={grouping.viewAll}
                                numEntries={sortedFilteredData[grouping.type].length}
                                table={pageIdentifier}
                            />
                        }
                    </React.Fragment>
                )
            })
        }
        </>
    }

    const updateRenderCount = () => {
        // added render count to stop 'no projects' result from showing on mount, since there are no filtered projects by default
        // page consistently finishes loading projects after 11 renders
        if (renderCount < 12) renderCount += 1;
        return null;
    }

    // Determines if no projects exist for a filter
    const checkIfNoProjectsMatchFilter = () => {
        if (data.length <= 0 || isFetchingData) return true;

        for (let group in sortedFilteredData) {
            if (sortedFilteredData[group].length > 0) {
                return true;
            }
        }
        return false;
    }

    // Renders the accordions with row indicating number of projects in a given filter
    const displayProjects = () => {
        return (
        (filteredProjects > 0 || !(renderCount > 11 && !checkIfNoProjectsMatchFilter() && filtersApplied.length > 0)) ?
            <tbody className="tbody-test">
                {renderData()}
                <tr className="tab-row border-0"></tr>
            </tbody>
        :
            <div className="no-projects-found">
                <div>
                    <img src={NoProjectsImage} alt="No Projects Found"></img>
                </div>
                <span>
                    {setNotFoundMsg()}
                </span>
                <p></p>
                <span className='clear-filter-link' onClick={(e) => clearProjectsFilter()}>
                    Clear Filter
                </span>
            </div>
        )
    }

    // PRIMARY RENDER FOR PROJECTS PAGE
    return (
        <>
            <div className="expandable-table-main">
                <table className="expandable-table-element">
                <TableHeader pageIdentifier={pageIdentifier} labels={labels} />
                {updateRenderCount()}
                {
                    data && !isFetchingData ?
                        displayProjects()
                    :
                        <tbody>
                            <tr>
                                <td>
                                    <div className="pulse-loader">
                                        <PulseLoader />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                }
                </table>
            </div>
        </>
    );
};

ExpandableTableNew.propTypes = {
    data: PropTypes.array,
    selectedData: PropTypes.array,
    isFetchingData: PropTypes.bool,
    groupBy: PropTypes.string,
    addMultipleSelectedDataItems: PropTypes.func,
    deleteMultipleSelectedDataItems: PropTypes.func,
    sortedFilteredData: PropTypes.object,
    filtersApplied: PropTypes.array,
    tableDataGroups: PropTypes.object,
    callback: PropTypes.func,
    keyFunction: PropTypes.func,
    pageIdentifier: PropTypes.string,
    clearProjectsFilter: PropTypes.func,
    refreshTable: PropTypes.func,
    TableRow: PropTypes.elementType,
    projectsCount: PropTypes.shape({
        all: PropTypes.number,
        active: PropTypes.number,
        archived: PropTypes.number,
        completed: PropTypes.number
    }),
    labels: PropTypes.shape({
        ProjectSingular: PropTypes.string,
        ProjectPlural: PropTypes.string,
        ProjectSingularPossessive: PropTypes.string,
        ProjectPluralPossessive: PropTypes.string,
        TaskSingular: PropTypes.string,
        TaskPlural: PropTypes.string,
        TaskSingularPossessive: PropTypes.string,
        TaskPluralPossessive: PropTypes.string,
        FormSingular: PropTypes.string,
        FormPlural: PropTypes.string,
        FormSingularPossessive: PropTypes.string,
        FormPluralPossessive: PropTypes.string,
    }),
};

export default ExpandableTableNew;
